import { t } from '@lingui/core/macro';
import { Box, Collapse, List, ListItemButton, ListItemText, styled, useMediaQuery } from '@mui/material';
import { ArrowRightToLineIcon } from 'lucide-react';
import React, { type JSX } from 'react';

import { usePersistedState } from '../../hooks/usePersistedState';
import { type AppLinkOptions, useCanOpen, useRouteMatches } from '../../routing';
import { linkable } from '../../routing/links/linkable';
import { asLinkProps } from '../../routing/links/linkOptions';
import { colorSystem } from '../../theme';
import { EASE_OUT, MOTION_MS } from '../AppSidebar/AppSidebar';
import { TertiaryIconButton } from '../buttons';
import { Flexbox } from '../Flexbox';
import type { IconElement } from '../icons/IconElement';
import { StickyLayout } from '../layout/StickyLayout';
import { ListItemIcon } from '../ListItemIcon';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';

type SidebarTarget = {
    /** Build it with `linkOptions()`. */
    link: AppLinkOptions;
};

export type CollapsibleSidebarItem = SidebarTarget & {
    type: 'link';
    label: string | JSX.Element;
    /** Drawn at 16px, the line height of the label, in the item's color. */
    icon: IconElement;
    disabled?: boolean;
    hidden?: boolean;
    endAdornment?: React.ReactNode;
    /**
     * Override the default selection behavior.
     * By default, an item is selected when its route is the current one, exactly.
     * `pathname` is the page on screen's, as a link writes it (the leaf's, from `useRouteMatches()`).
     */
    isSelected?: (pathname: string) => boolean;
    /** Indented sub-items under this link, shown whenever any is visible: there is no expand/collapse toggle. */
    subItems?: SidebarSubItem[];
    /**
     * Draws the item as its sub-items' group: while the sidebar is open, their heading, neither a link nor marked.
     * Collapsed to the rail, where they are hidden, it links to `link` and is marked while one of them is, through
     * their `isSelected`: the item's own `isSelected` isn't used.
     */
    group?: boolean;
};

type SidebarSubItem = SidebarTarget & {
    label: string;
    hidden?: boolean;
    /**
     * Overrides the default check (its route or one beneath it); needed when a sibling's route extends this one.
     * `pathname` is the page on screen's, as a link writes it (the leaf's, from `useRouteMatches()`).
     */
    isSelected?: (pathname: string) => boolean;
};

interface CollapsibleSidebarProps {
    id: string;
    /** A string draws as the sidebar's heading; a node, such as a project's lines, draws as given. */
    title: React.ReactNode;
    contents: Array<CollapsibleSidebarItem>;
    intialOpen?: boolean;
    /**
     * `gray` for a second sidebar beside the app's, outside the page's panel: its gray, a line to it, and the page on
     * screen filled white, as there.
     */
    surface?: 'white' | 'gray';
    /** False for a menu that is always open, without the collapse button. */
    collapsible?: boolean;
    /** Whether it is open, with `onOpenChange`, in place of the choice it keeps itself. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Opens and closes without motion, such as after a keyboard shortcut. */
    instant?: boolean;
}

/** The sticky title row: its 20px top padding above the 40px collapse button. */
const TITLE_ROW_HEIGHT = '60px';

/** The line height of the 12px labels: a larger icon looks too big next to them. */
const ICON_SIZE = 16;

/**
 * Its width's and its labels' motion: the app sidebar's, so beside it they move as one. None when `instant`, or for a
 * user who asks for reduced motion.
 */
function useMotion(instant = false): { timeout: number; easing: string } {
    const isStill = useMediaQuery('(prefers-reduced-motion: reduce)');
    return { timeout: isStill || instant ? 0 : MOTION_MS, easing: EASE_OUT };
}

/** Colors an item's icon, which passes no color; the negative margin centers it in the collapsed sidebar. */
const ItemIcon = styled(ListItemIcon)({
    color: 'inherit',
    marginLeft: '-4px',
    marginRight: '6px',
    '& > svg': {
        color: 'inherit',
    },
});

/** An item's icon and label, without the states of a link: more room above it than below, where its group starts. */
const GroupHeading = styled('div')({
    display: 'flex',
    alignItems: 'center',
    color: colorSystem.neutral[7],
    padding: '4px 0px 6px',
    margin: '6px 0px 0px',
});

/** Hover as on the app's sidebar beside it: a fill under the pointer. The theme's focus ring sits inside the row. */
const rowStates = {
    '&:hover': {
        color: colorSystem.neutral[9],
        background: colorSystem.neutral[2],
    },
    // The page on screen keeps its mark under the pointer too, as in the app sidebar; MUI's own rule would fade it.
    '&.Mui-selected, &.Mui-selected:hover': {
        color: colorSystem.primary[7],
        background: colorSystem.primary[1],
    },
    '[data-surface="gray"] &.Mui-selected, [data-surface="gray"] &.Mui-selected:hover': {
        color: colorSystem.neutral[9],
        background: colorSystem.neutral.white,
        boxShadow: `0 1px 2px rgba(42, 49, 71, 0.08), 0 0 0 1px ${colorSystem.neutral[2]}`,
    },
};

const StyledListItemButton = styled(ListItemButton)({
    color: colorSystem.neutral[7],
    padding: '6px 0px',
    margin: '4px 0px',
    borderRadius: '8px',
    overflow: 'hidden',
    ...rowStates,
});

/** A 28px row, all of it the target. Its label starts where an item's does: past its icon's 32px, -4px and +6px. */
const StyledSubItemButton = styled(ListItemButton)({
    color: colorSystem.neutral[7],
    padding: '6px 0px 6px 34px',
    margin: 0,
    borderRadius: '8px',
    ...rowStates,
});

const LinkItemButton = linkable(StyledListItemButton);
const LinkSubItemButton = linkable(StyledSubItemButton);

/**
 * The mark of an entry whose caller decides it through `isSelected`. The router names the current page only on a link
 * at its own URL, and an entry marked this way needn't be one: a page at its second route isn't.
 */
const markedProps = (isMarked: boolean | undefined, holdsPages = false) => ({
    selected: isMarked,
    // An entry over pages holds the current page rather than being it, or a screen reader would hear two.
    ...(isMarked && { 'aria-current': holdsPages ? ('true' as const) : ('page' as const) }),
});

export function CollapsibleSidebar({
    title,
    contents,
    id,
    intialOpen = true,
    surface = 'white',
    collapsible = true,
    open,
    onOpenChange,
    instant,
}: CollapsibleSidebarProps): JSX.Element {
    const [isOpenAsChosen, setIsOpen] = usePersistedState(id, intialOpen);
    // Also where a user collapsed it before it stopped being collapsible.
    const isOpen = !collapsible || (open ?? isOpenAsChosen);
    const canOpen = useCanOpen();
    const visibleContents = contents.filter((item) => !item.hidden && canOpen(item.link));
    const background = surface === 'gray' ? colorSystem.neutral[1] : colorSystem.neutral.white;
    const motion = useMotion(instant);

    return (
        <StickyLayout
            style={{
                ...(surface === 'gray'
                    ? { borderLeft: `1px solid ${colorSystem.neutral[2]}` }
                    : { borderRight: `1px solid ${colorSystem.neutral[2]}` }),
                backgroundColor: background,
                zIndex: 3,
                height: '100%',
                // No top padding: the title row has it, so no row shows above the title while the list scrolls.
                padding: '0 12px 12px',
                transition: `width ${motion.timeout}ms ${motion.easing}`,
                boxSizing: 'border-box',
                // Pinned: the sub-items' `nowrap` text widens the sidebar past 64px even inside a closed <Collapse>.
                width: isOpen ? '200px' : '64px',
                overflowX: 'hidden',
                // A list taller than the page scrolls: sub-items always show, so nothing can be folded to make room.
                overflowY: 'auto',
                // A row that takes the focus scrolls into view below the sticky title row, not under it.
                scrollPaddingTop: TITLE_ROW_HEIGHT,
            }}
        >
            {/* As tall as its rows, so the title row sticks down to the last one. The title row and the sidebar pad it. */}
            <List disablePadding data-surface={surface}>
                <Flexbox
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        // Stays while the list scrolls beneath it, so the collapse button is always in reach.
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        minHeight: TITLE_ROW_HEIGHT,
                        boxSizing: 'border-box',
                        paddingTop: '20px',
                        background,
                    }}
                >
                    {typeof title === 'string' ? (
                        <Collapse in={isOpen} orientation="horizontal" {...motion}>
                            <Text variant={'h3'} showEllipsis={'ellipsisOnly'}>
                                {title}
                            </Text>
                        </Collapse>
                    ) : (
                        isOpen && <Box sx={{ flex: 1, minWidth: 0, paddingBottom: '8px' }}>{title}</Box>
                    )}
                    {collapsible && (
                        <TertiaryIconButton
                            aria-label={isOpen ? t`Collapse sidebar` : t`Expand sidebar`}
                            onClick={() => (onOpenChange ?? setIsOpen)(!isOpen)}
                            disableRipple
                        >
                            <ArrowRightToLineIcon style={{ transform: isOpen ? 'rotate(180deg)' : undefined }} />
                        </TertiaryIconButton>
                    )}
                </Flexbox>

                {visibleContents.map((item, i) => (
                    <CollapsibleSidebarListItem key={i} isOpen={isOpen} instant={instant} {...item} />
                ))}
            </List>
        </StickyLayout>
    );
}

export function CollapsibleSidebarListItem({
    link,
    label,
    icon,
    isOpen,
    disabled,
    isSelected,
    endAdornment,
    subItems,
    group,
    instant,
}: CollapsibleSidebarItem & { isOpen: boolean; selected?: boolean; instant?: boolean }): JSX.Element {
    const pathname = useRouteMatches().at(-1)?.pathname ?? '';
    const canOpen = useCanOpen();
    const visibleSubItems = subItems?.filter((item) => !item.hidden && canOpen(item.link)) ?? [];
    // shortcut: a group is marked in the rail only through its sub-items' isSelected; check the route too when a group
    // caller passes sub-items without one.
    const isGroupMarked = visibleSubItems.some((subItem) => subItem.isSelected?.(pathname));
    const headingId = React.useId();
    const motion = useMotion(instant);
    const content = (
        <>
            <ItemIcon iconSize={ICON_SIZE}>{icon}</ItemIcon>

            <Collapse
                in={isOpen}
                orientation="horizontal"
                {...motion}
                sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    flex: isOpen ? 1 : '0 0 auto',
                    '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
                        minWidth: 0,
                        width: isOpen ? '100%' : undefined,
                    },
                }}
            >
                <Flexbox
                    gap="4px"
                    sx={{
                        alignItems: 'center',
                        minWidth: 0,
                        overflow: 'hidden',
                        width: '100%',
                    }}
                >
                    <ListItemText
                        sx={{ minWidth: 0, flex: '0 1 auto' }}
                        primary={
                            <Text
                                variant="h5"
                                showEllipsis
                                style={{
                                    color: 'inherit',
                                    display: 'block',
                                    minWidth: 0,
                                    paddingRight: endAdornment ? undefined : '8px',
                                }}
                            >
                                {label}
                            </Text>
                        }
                    />
                    {endAdornment && <Flexbox sx={{ flexShrink: 0, alignItems: 'center' }}>{endAdornment}</Flexbox>}
                </Flexbox>
            </Collapse>
        </>
    );

    const subItemList = visibleSubItems.length > 0 && (
        <Collapse in={isOpen} {...motion} {...(group && isOpen && { role: 'group', 'aria-labelledby': headingId })}>
            {visibleSubItems.map((subItem, i) => (
                <CollapsibleSidebarSubItem key={i} {...subItem} />
            ))}
        </Collapse>
    );

    if (group && isOpen) {
        return (
            <>
                <GroupHeading>
                    <ItemIcon iconSize={ICON_SIZE}>{icon}</ItemIcon>
                    <Text
                        id={headingId}
                        variant="h5"
                        showEllipsis
                        style={{ color: 'inherit', display: 'block', minWidth: 0, paddingRight: '8px' }}
                    >
                        {label}
                    </Text>
                </GroupHeading>
                {subItemList}
            </>
        );
    }

    return (
        <>
            <Tooltip variant="white" title={label} disableHoverListener={isOpen} placement="right">
                <span>
                    <LinkItemButton
                        {...asLinkProps(link)}
                        disableRipple
                        disabled={disabled}
                        // In the rail the label is hidden, which leaves the link without a name.
                        aria-label={!isOpen && typeof label === 'string' ? label : undefined}
                        {...markedProps(group ? isGroupMarked : isSelected?.(pathname), visibleSubItems.length > 0)}
                        activeProps={isSelected || group ? undefined : { selected: true }}
                        activeOptions={{ exact: true, includeSearch: false }}
                    >
                        {content}
                    </LinkItemButton>
                </span>
            </Tooltip>

            {subItemList}
        </>
    );
}

function CollapsibleSidebarSubItem({ link, label, isSelected }: SidebarSubItem): JSX.Element {
    const pathname = useRouteMatches().at(-1)?.pathname ?? '';

    return (
        <LinkSubItemButton
            {...asLinkProps(link)}
            disableRipple
            {...markedProps(isSelected?.(pathname))}
            activeProps={isSelected ? undefined : { selected: true }}
            activeOptions={{ exact: false, includeSearch: false }}
        >
            {/* Without `ListItemText`, whose margins and 20px line would make the row 36px. */}
            <Text
                variant="body-small"
                style={{
                    color: 'inherit',
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {label}
            </Text>
        </LinkSubItemButton>
    );
}
